import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import { actions as campaignActions } from 'store/campaign';
import { actions as userMessageActions } from 'store/userMessaging';
import { dismissButton, dispatchButton } from 'helpers/error/buttonBuilders';
import { TableComponentV2, ToolbarV2 } from 'components';
import filterHelpers from 'store/helpers/filterHelpers';
import { campaignTableTitles } from 'constants.js';
import { resetListsData } from 'store/lists/actions';
import { getEnvVar } from 'helpers';
import { campaignOccurrenceTypeOptions } from 'views/Campaign/constants';

const {
  getPaginatedCampaigns,
  deleteCampaigns,
  unsetCurrentCampaign,
  setCurrentCampaign,
  setCopyCurrentCampaign,
  updateCampaignFilters,
  resetCampaignFilters
} = campaignActions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  }
}));

const Campaign = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    onClickToggleArchive,
    onClickAdd,
    onClickEdit,
    onClickCopy,
    isTemplateReadOnly
  } = filterHelpers();
  const action = getPaginatedCampaigns({ force: true });
  const campaigns = useSelector(store => store.campaign.paginatedList);
  const filters = useSelector(
    store => store.campaign.filters
  );
  const isLoading = useSelector(store => store.campaign.gettingPaginated);
  const productNames = useSelector(
    store => store.events.supportedEventTypes.productNames
  );
  const productCategories = useSelector(
    store => store.events.supportedEventTypes.productCategories
  );
  const optionsToFilterType = [
    { name: 'type', value: 'EMAIL', checked: false },
    { name: 'type', value: 'SMS', checked: false }
  ];
  const optionsToFilterTemplate = [
    {
      name: 'searchScopes',
      searchScope: 'TENANT DEFAULT',
      value: 'tenant',
      checked: false
    },
    {
      name: 'searchScopes',
      searchScope: 'PROPERTY LEVEL',
      value: 'property',
      checked: false
    }
  ];
  const optionsToFilterProductNames = productNames
    ? productNames.map(productName => {
        return { name: 'productName', value: productName, checked: false };
      })
    : [];
  const optionsToFilterProductCategories = productCategories
    ? productCategories.map(productCategory => {
        return {
          name: 'productCategory',
          value: productCategory,
          checked: false
        };
      })
    : [];
  const optionsToFilterOccurenceTypes = campaignOccurrenceTypeOptions
    ? campaignOccurrenceTypeOptions.map(occurenceType => {
      return {
        name: 'occurenceType',
        value: occurenceType.value,
        checked: false
      }
    })
    : [];
  const optionsToFilterMarketingType = [
    { name: 'marketingType', value: 'Promotional', checked: false },
    { name: 'marketingType', value: 'Transactional', checked: false }
  ];

  const expirationDays =
    parseInt(getEnvVar('ADM_REPORT_EXPIRATION_DAYS')) || 60;
  const expirationMonthsInDays = 
    parseInt(getEnvVar('ADM_REPORT_EXPIRATION_MONTHS_IN_DAYS')) || 730;

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    dispatch(getPaginatedCampaigns({ force: true }));
    dispatch(resetListsData());
  }, []);

  const onClickArchive = uuid => {
    let action = deleteCampaigns({
      campaignUuids: [uuid],
      activeStatus: filters?.displayActive
    });

    if (filters?.displayActive) {
      dispatch(
        userMessageActions.setMessage({
          title: 'Do you wish to continue?',
          severity: 'warning',
          instructions: `This campaign can be unarchived anytime.`,
          buttons: [
            dispatchButton('Archive Campaign', action),
            dismissButton('Cancel')
          ]
        })
      );
    } else {
      dispatch(action);
    }
  };

  const getButtonsGroup = () => [
    {
      name: `View ${filters?.displayActive ? 'Archived' : 'Active'} Campaign`,
      action: () => onClickToggleArchive(action, updateCampaignFilters, { displayActive: !filters?.displayActive, pageNo: 0 })
    },
    {
      name: 'Add Campaign',
      action: () => onClickAdd(unsetCurrentCampaign(), `/campaigns/settings`)
    }
  ];

  const isInvalidEndDate = (startDate, endDate) => {
    let date = new Date();
    date.setDate(date.getDate() - expirationMonthsInDays);
    return new Date(endDate) < date || new Date(startDate) >= new Date();
  };

  const getActionButtons = Campaign => [
    {
      name: isTemplateReadOnly(Campaign, isLoading)
        ? 'View Campaign'
        : 'Edit Campaign',
      action: ['ACTION', () => onEdit(Campaign)],
      ...(isTemplateReadOnly(Campaign, isLoading) && {
        tooltipPlacement: 'left-start',
        tooltipTitle:
          "Tenant Default Campaign can be edited only with 'ALL' property"
      })
    },
    {
      name: 'Copy Campaign',
      action: ['ACTION', () => onCopy(Campaign.uuid)]
    },
    {
      name: 'View Campaign Report',
      action: [
        'GO',
        `/reports/campaigns/${Campaign.uuid}/${Campaign.type}/details`
      ],
      ...(isInvalidEndDate(Campaign.startDate, Campaign.endDate) && {
        isDisabled: true,
        tooltipTitle: new Date(Campaign.startDate) >= new Date()
          ? `${Campaign.name} has not started yet`
          : (Campaign.active === false
            ? `${Campaign.name} ended more than ${expirationMonthsInDays} days ago`
            : Campaign.name)
      })
    },
    {
      name: filters?.displayActive ? 'Archive Campaign' : 'Activate Campaign',
      action: ['ACTION', () => onClickArchive(Campaign.uuid)],
      ...(isTemplateReadOnly(Campaign, isLoading) && {
        isDisabled: true,
        tooltipPlacement: 'left-start',
        tooltipTitle:
          "Tenant Default Campaign can be edited only with 'ALL' property"
      })
    }
  ];

  const filterOptions = {
    filters: [
      {
        options: optionsToFilterType.map(option => {
          if (filters?.filterTypes?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Type',
        values: filters?.filterTypes,
        onChange: types => dispatch(updateCampaignFilters({ filterTypes: types, pageNo: 0 }))
      },
      {
        options: optionsToFilterTemplate.map(option => {
          if (filters?.filterTemplates?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Hierarchy',
        values: filters?.filterTemplates,
        onChange: templates => dispatch(updateCampaignFilters({ filterTemplates: templates, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductNames.map(option => {
          if (filters?.filterProductNames?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Name',
        values: filters?.filterProductNames,
        onChange: productNames =>
          dispatch(updateCampaignFilters({ filterProductNames: productNames, pageNo: 0 }))
      },
      {
        options: optionsToFilterProductCategories.map(option => {
          if (filters?.filterProductCategories?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Product Category',
        values: filters?.filterProductCategories,
        onChange: productCategories =>
          dispatch(updateCampaignFilters({ filterProductCategories: productCategories, pageNo: 0 }))
      },
      {
        options: optionsToFilterOccurenceTypes.map(option => {
          if (filters?.filterOccurrenceTypes?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Occurrence Type',
        values: filters?.filterOccurrenceTypes,
        onChange: occurenceTypes => 
          dispatch(updateCampaignFilters({ filterOccurrenceTypes: occurenceTypes, pageNo: 0 }))
      },
      {
        options: optionsToFilterMarketingType.map(option => {
          if (filters?.filterMarketingTypes?.includes(option.value)) {
            option.checked = true;
          }
          return option;
        }),
        title: 'Marketing Type',
        values: filters?.filterMarketingTypes,
        onChange: marketingTypes => 
          dispatch(updateCampaignFilters({ filterMarketingTypes: marketingTypes, pageNo: 0 }))
      }
    ],
    resetFilters: () => dispatch(resetCampaignFilters())
  };

  const searchOptions = {
    searchPlaceholder: 'Search Campaigns',
    searchAction: (payload) => dispatch(updateCampaignFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const onEdit = row =>
    onClickEdit(
      [unsetCurrentCampaign(), setCurrentCampaign(row)],
      `/campaigns/edit/${row.uuid}`
    );
  const onCopy = uuid =>
    onClickCopy(setCopyCurrentCampaign, `/campaigns/settings`, uuid, campaigns);

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        buttonsGroup={getButtonsGroup()}
        action={getPaginatedCampaigns({ force: true })}
      />
      <div className={classes.content}>
        <TableComponentV2
          isLoading={isLoading}
          tableTitles={campaignTableTitles}
          onDoubleClickRow={row => onEdit(row)}
          actionButtonOptions={row => getActionButtons(row)}
          filteredRows={campaigns}
          action={getPaginatedCampaigns({ force: true })}
          filterValues={filters}
          filterAction={(payload) => dispatch(updateCampaignFilters(payload))}
        />
      </div>
    </div>
  );
};

export default Campaign;
